// _ui-controls.ts — wspólne kontrolki UI (jedno źródło prawdy)
// ─────────────────────────────────────────────────────────────────────────────
// Cel: spójny, profesjonalny wygląd ON/OFF + liczb + kart w całych Automatyzacjach
// (Proste reguły / Auto-odpowiedzi / Wiadomości pozakupowe) i wszędzie indziej.
//
//   • sbSwitch(id, checked, extraAttr)         → iOS-owy włącznik on/off
//   • sbStepper(id, value, min, max, opts)     → stepper „− N +"
//   • sbBadge(on, onLabel, offLabel)           → badge stanu (zielony/szary)
//   • sbIconTile(icon, on, opts)               → kafelek z ikoną (akcent gdy ON)
//   • bindSbSteppers(root, onChange)           → delegowana obsługa − / +
//   • sbStepperValue(el)                       → odczyt liczby ze steppera
//   • ensureSbControlsCss()                    → wstrzyknięcie CSS (raz, id-guard)
//
// Markup jest celowo „głupi" (czyste stringi HTML) — działa z każdym rendererem.
// Logikę wpinają konsumenci (change na inputach switchy, bindSbSteppers na +/−).
// ─────────────────────────────────────────────────────────────────────────────

const SB_CONTROLS_CSS = `
.sb-switch{position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;cursor:pointer;vertical-align:middle}
.sb-switch input{position:absolute;opacity:0;width:0;height:0}
.sb-switch .sb-track{position:absolute;inset:0;background:var(--color-surface-2);border:1px solid var(--color-border);border-radius:999px;transition:background .2s,border-color .2s}
.sb-switch .sb-track::before{content:"";position:absolute;height:18px;width:18px;left:3px;top:2px;background:#9ca3af;border-radius:50%;transition:transform .2s,background .2s;box-shadow:0 1px 2px rgba(0,0,0,.3)}
.sb-switch input:checked + .sb-track{background:var(--color-primary);border-color:var(--color-primary)}
.sb-switch input:checked + .sb-track::before{transform:translateX(20px);background:#fff}
.sb-switch input:disabled + .sb-track{opacity:.45;cursor:not-allowed}
[data-sync-wrap] .sb-switch input:checked + .sb-track{background:#22c55e;border-color:#22c55e}

.sb-stepper{display:inline-flex;align-items:center;border:1px solid var(--color-border);border-radius:8px;overflow:hidden;background:var(--color-surface-2);vertical-align:middle}
.sb-stepper button{width:30px;height:30px;border:none;background:transparent;color:var(--color-text);font-size:16px;cursor:pointer;line-height:1;padding:0;transition:background .15s}
.sb-stepper button:hover{background:var(--color-surface-hover)}
.sb-stepper .sb-step-val{min-width:44px;text-align:center;font-size:13px;font-weight:700;font-variant-numeric:tabular-nums;border-left:1px solid var(--color-border);border-right:1px solid var(--color-border);height:30px;line-height:30px;padding:0 4px;color:var(--color-text)}
.sb-stepper input.sb-step-val{background:transparent;outline:none;border-top:none;border-bottom:none}
.sb-step-off{opacity:.4;pointer-events:none}

.sb-badge{display:inline-block;font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;line-height:1.5;vertical-align:middle}
.sb-badge.on{background:rgba(34,197,94,.15);color:#22c55e}
.sb-badge.off{background:var(--color-surface-2);color:var(--color-text-muted)}

.sb-icontile{width:36px;height:36px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:var(--color-surface-2);transition:background .2s}
.sb-icontile i,.sb-icontile svg{width:18px;height:18px;color:var(--color-text-muted);transition:color .2s}
.sb-icontile.on{background:rgba(99,102,241,.15)}
.sb-icontile.on i,.sb-icontile.on svg{color:var(--color-primary)}

/* Spójna karta ustawień: kafelek ikony + tytuł + badge + kontrolka po prawej */
.sb-setting-card{border:1px solid var(--color-border);border-radius:12px;background:var(--color-surface);padding:16px;transition:border-color .2s}
.sb-setting-card.is-on{border-color:rgba(99,102,241,.4)}
.sb-setting-row{display:flex;align-items:flex-start;gap:14px}
.sb-setting-main{flex:1;min-width:0}
.sb-setting-head{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap}
.sb-setting-title{font-size:14px;font-weight:700;color:var(--color-text);display:flex;align-items:center;gap:8px}
.sb-setting-desc{font-size:12px;color:var(--color-text-muted);line-height:1.5;margin-top:4px;max-width:680px}
.sb-setting-ctl{display:flex;align-items:center;gap:10px;margin-top:12px}
.sb-setting-ctl-label{font-size:12px;color:var(--color-text-secondary)}
`;

export function ensureSbControlsCss(): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById('sb-controls-css')) return;
    const style = document.createElement('style');
    style.id = 'sb-controls-css';
    style.textContent = SB_CONTROLS_CSS;
    document.head.appendChild(style);
}

/** iOS-owy włącznik on/off. `extraAttr` np. 'data-x="1"' trafia na <label>. */
export function sbSwitch(id: string, checked: boolean, extraAttr?: string): string {
    return '<label class="sb-switch" for="' + id + '"' + (extraAttr ? ' ' + extraAttr : '') + '>'
        + '<input type="checkbox" id="' + id + '"' + (checked ? ' checked' : '') + '>'
        + '<span class="sb-track"></span></label>';
}

export interface SbStepperOpts { suffix?: string; disabled?: boolean; }

/** Stepper „− N +". Wartość trzymana w <span class="sb-step-val" id={id}>.
 *  `suffix` (np. ' min') wyświetlany przy liczbie. Obsługę +/− wpina bindSbSteppers. */
export function sbStepper(id: string, value: number, min: number, max: number, opts?: SbStepperOpts): string {
    const suffix = (opts && opts.suffix) || '';
    const off = (opts && opts.disabled) ? ' sb-step-off' : '';
    return '<div class="sb-stepper' + off + '" data-sb-stepper="1" data-min="' + min + '" data-max="' + max + '"'
        + (suffix ? ' data-suffix="' + suffix.replace(/"/g, '') + '"' : '') + '>'
        + '<button type="button" data-sb-step="-1" aria-label="mniej">−</button>'
        + '<span class="sb-step-val" id="' + id + '">' + value + suffix + '</span>'
        + '<button type="button" data-sb-step="1" aria-label="więcej">+</button>'
        + '</div>';
}

/** Odczyt liczby z elementu .sb-step-val (parsuje, ignoruje suffix). */
export function sbStepperValue(el: Element | null): number {
    if (!el) return NaN;
    const m = ((el.textContent || '').match(/-?\d+/) || [])[0];
    return m == null ? NaN : parseInt(m, 10);
}

/** Badge stanu (Włączona/Wyłączona itp.) — zielony gdy on, szary gdy off. */
export function sbBadge(on: boolean, onLabel: string, offLabel: string, id?: string): string {
    return '<span class="sb-badge ' + (on ? 'on' : 'off') + '"' + (id ? ' id="' + id + '"' : '') + '>'
        + (on ? onLabel : offLabel) + '</span>';
}

/** Aktualizuje badge stanu w miejscu (klasa + tekst). */
export function sbUpdateBadge(el: Element | null, on: boolean, onLabel: string, offLabel: string): void {
    if (!el) return;
    el.classList.toggle('on', on);
    el.classList.toggle('off', !on);
    el.textContent = on ? onLabel : offLabel;
}

/** Kafelek z ikoną lucide (akcent gdy ON). */
export function sbIconTile(icon: string, on?: boolean, id?: string): string {
    return '<div class="sb-icontile' + (on ? ' on' : '') + '"' + (id ? ' id="' + id + '"' : '') + '>'
        + '<i data-lucide="' + icon + '"></i></div>';
}

/** Delegowana obsługa − / + dla wszystkich .sb-stepper w `root`.
 *  onChange(value, stepValEl) wołane po każdej zmianie (z aktualną liczbą). */
export function bindSbSteppers(root: ParentNode, onChange: (value: number, valEl: HTMLElement, stepper: HTMLElement) => void): void {
    root.querySelectorAll('.sb-stepper[data-sb-stepper]').forEach(function(stepper: Element) {
        const st = stepper as HTMLElement;
        const min = parseInt(st.getAttribute('data-min') || '0', 10);
        const max = parseInt(st.getAttribute('data-max') || '999999', 10);
        const suffix = st.getAttribute('data-suffix') || '';
        const valEl = st.querySelector('.sb-step-val') as HTMLElement | null;
        if (!valEl) return;
        st.querySelectorAll('[data-sb-step]').forEach(function(btn: Element) {
            btn.addEventListener('click', function() {
                if (st.classList.contains('sb-step-off')) return;
                const step = parseInt((btn as HTMLElement).getAttribute('data-sb-step') || '0', 10);
                let v = sbStepperValue(valEl);
                if (!Number.isFinite(v)) v = min;
                v = Math.max(min, Math.min(max, v + step));
                valEl.textContent = String(v) + suffix;
                onChange(v, valEl, st);
            });
        });
    });
}
